export const OVERFLOW_THRESHOLD: number = 10;
export const FILTERS_GAP: number = 12;
export const RESET_BUTTON_SPACE: number = 62;
export const MOBILE_SCREEN_WIDTH: number = 768;

export interface FilterOverflowResult {
  /** Indexes (into the measured children array) of items that overflow. */
  overflowIndexes: number[];
  /** Rounded-plus-gap widths for each overflowing item, in the same order. */
  hiddenWidths: number[];
  /** Sum after accumulating all items — keeps accumulating past the overflow point. */
  filtersWidthSum: number;
}

export interface FilterPullBackResult {
  /** How many dropdown children (from the front of the snapshot) to move back. */
  pullBackCount: number;
  /** The hidden-widths stack after the corresponding pops (input is not mutated). */
  hiddenWidths: number[];
  /** Updated running sum after pull-backs. */
  filtersWidthSum: number;
}

export function getInitialFiltersWidthSum(rightFiltersWidth: number, moreFilterWidth: number): number {
  return rightFiltersWidth + moreFilterWidth + RESET_BUTTON_SPACE + OVERFLOW_THRESHOLD;
}

export function computeOverflowingFilters(
  rawItemWidths: number[],
  filterRowWidth: number,
  initialFiltersWidthSum: number
): FilterOverflowResult {
  const overflowIndexes: number[] = [];
  const hiddenWidths: number[] = [];
  let filtersWidthSum: number = initialFiltersWidthSum;

  rawItemWidths.forEach((rawWidth, index) => {
    const itemWidth: number = Math.round(rawWidth) + FILTERS_GAP;

    filtersWidthSum += itemWidth;
    if (filtersWidthSum > filterRowWidth) {
      hiddenWidths.push(itemWidth);
      overflowIndexes.push(index);
    }
  });

  return { overflowIndexes, hiddenWidths, filtersWidthSum };
}

export function shouldSkipMoveToDropdown(
  overflowIndexes: number[],
  rawItemWidths: number[],
  moreFilterWidth: number,
  dropdownChildrenCount: number
): boolean {
  return (
    overflowIndexes.length === 1 &&
    rawItemWidths[overflowIndexes[0]] < moreFilterWidth &&
    dropdownChildrenCount === 0
  );
}

export function computePullBack(
  dropdownChildrenCount: number,
  hiddenWidths: number[],
  filtersWidthSum: number,
  filterRowWidth: number
): FilterPullBackResult {
  const remainingWidths: number[] = [...hiddenWidths];
  let sum: number = filtersWidthSum;
  let pullBackCount: number = 0;

  // Iterates once per snapshotted dropdown child, peeking the last hidden
  // width each time. Stops as soon as the stack is empty or the next width no
  // longer fits, since the running sum only grows.
  for (let i = 0; i < dropdownChildrenCount; i++) {
    if (remainingWidths.length === 0) break;

    const itemWidth: number = remainingWidths[remainingWidths.length - 1];
    if (sum + itemWidth < filterRowWidth) {
      sum += itemWidth;
      remainingWidths.pop();
      pullBackCount++;
    }
  }

  return { pullBackCount, hiddenWidths: remainingWidths, filtersWidthSum: sum };
}
